import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import UploadExcelDialog from "../Schedules/utils/UploadExcelEnhanced";
import { generateExcelTemplate } from "../../utils/data/excelData";
import {
  getPriorYAHeaders,
  parsePriorYARows,
  PRIOR_YA_SECTIONS,
} from "./utils/priorYAExcel";

const PriorYAExcelUpload = ({
  company,
  currency,
  currencyRate,
  isCrossYA,
  onUpload,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [alert, setAlert] = useState({ message: "", severity: "info" });
  const [errorFileUrl, setErrorFileUrl] = useState(null);
  const request = useRef(0);
  const headers = getPriorYAHeaders(isCrossYA);
  const instructions = [
    ["Col", "Comments"],
    [
      "A",
      "Section: CA, Losses or Donations (case-sensitive). Any other value is invalid.",
    ],
    [
      "B",
      `For CA: prior YA in YYYY format, before ${company.yearOfAssessment}. For Losses / Donations: financial year ended in DD/MM/YYYY format, in ${Number(company.yearOfAssessment) - 2} or earlier. Excel date cells are also supported.`,
    ],
    [
      "C",
      `Non-negative ${isCrossYA ? "preceding YA " : ""}amount in ${currency}.`,
    ],
    ...(isCrossYA
      ? [
          [
            "D",
            `Non-negative current YA amount in ${currency}. Enter 0 if unused.`,
          ],
        ]
      : []),
    [
      isCrossYA ? "E" : "D",
      "Optional reference. Uploaded rows are appended to existing rows; uploading again adds them again.",
    ],
    [isCrossYA ? "F" : "E", "Exchange Rate: if your Main Tax Computation is in SGD, leave blank; this column is ignored. For non-SGD, enter a positive historical exchange rate or leave blank to use the Main Tax Computation exchange rate."],
    [isCrossYA ? "G" : "F", "Optional Forfeited: TRUE or FALSE (case-insensitive). Blank defaults to FALSE."],
    [isCrossYA ? "H" : "G", "Optional Declare Agreement Status: TRUE or FALSE (case-insensitive). Blank defaults to FALSE. When FALSE or blank, Agreed and Date of Notice of Assessment are ignored, regardless of their values."],
    [isCrossYA ? "I" : "H", "Optional Agreed: TRUE or FALSE (case-insensitive). When Declare Agreement Status is TRUE, blank defaults to FALSE."],
    [isCrossYA ? "J" : "I", "Optional Date of Notice of Assessment: DD/MM/YYYY or an Excel date cell. Leave blank if unavailable. Only used when Declare Agreement Status is TRUE."],
    ["Template", isCrossYA ? "Cross YA requires separate Preceding Amount and Current Amount columns. A single Amount template will be rejected. Keep all headers in their original order, including optional columns." : "Single YA requires one Amount column. A cross YA template will be rejected. Keep all headers in their original order, including optional columns."],
  ];
  useEffect(
    () => () => {
      request.current += 1;
    },
    [],
  );
  useEffect(
    () => () => {
      if (errorFileUrl) URL.revokeObjectURL(errorFileUrl);
    },
    [errorFileUrl],
  );
  const reset = () => {
    request.current += 1;
    setSelectedFile(null);
    setRows([]);
    setErrorFileUrl(null);
    setProcessing(false);
    setAlert({ message: "", severity: "info" });
  };
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    reset();
    const currentRequest = request.current;
    setSelectedFile(file);
    setProcessing(true);
    const reader = new FileReader();
    const fail = () => {
      if (currentRequest !== request.current) return;
      setRows([]);
      setProcessing(false);
      setAlert({
        message:
          "Unable to read this Excel file. Please use the downloaded template.",
        severity: "error",
      });
    };
    reader.onerror = fail;
    reader.onload = (e) => {
      if (currentRequest !== request.current) return;
      try {
        const workbook = XLSX.read(e.target.result, { type: "array" });
        const data = XLSX.utils.sheet_to_json(
          workbook.Sheets[workbook.SheetNames[0]],
          { header: 1 },
        );
        const result = parsePriorYARows(data, {
          isCrossYA,
          yearOfAssessment: company.yearOfAssessment,
          currency,
          currencyRate,
        });
        setRows(result.rows);
        if (result.errors.length) {
          setErrorFileUrl(
            URL.createObjectURL(
              new Blob([result.errors.join("\n")], { type: "text/plain" }),
            ),
          );
        }
        setAlert({
          message: result.errors.length
            ? `${result.errors.length} issues detected. ${result.rows.length} valid rows can be uploaded.`
            : result.rows.length
              ? `${result.rows.length} rows validated successfully.`
              : "No data found in the file.",
          severity: result.errors.length
            ? "error"
            : result.rows.length
              ? "success"
              : "error",
        });
        setProcessing(false);
      } catch {
        fail();
      }
    };
    reader.readAsArrayBuffer(file);
  };
  const downloadTemplate = (options = {}) => {
    const sampleData = PRIOR_YA_SECTIONS.map((section) => {
      const values = [
        section,
        section === "CA"
          ? Number(company.yearOfAssessment) - 1
          : `31/12/${Number(company.yearOfAssessment) - 2}`,
        ...(isCrossYA ? [1000, 0] : [1000]),
        "Prior year balance",
        currency === "SGD" ? "" : currencyRate,
        "",
        "",
        "",
        "",
      ];
      return Object.fromEntries(
        headers.map((header, i) => [header, values[i]]),
      );
    });
    const fileName =
      options.customFileName?.trim() ||
      `prior_ya_ca_losses_donations_${isCrossYA ? "cross_ya" : "single_ya"}_upload_template.xlsx`;
    generateExcelTemplate({
      fileName: fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`,
      instructions,
      sampleData,
      includeInstructions: options.includeInstructions ?? true,
      includeSampleData: options.includeSampleData ?? true,
    });
  };
  return (
    <UploadExcelDialog
      title='Prior YA CA, Losses and Donations'
      open
      onClose={onClose}
      selectedFile={selectedFile}
      processing={processing}
      uploading={false}
      validRows={rows.length}
      errorFileUrl={errorFileUrl}
      alertMessage={alert.message}
      alertSeverity={alert.severity}
      onCloseAlert={() => setAlert({ ...alert, message: "" })}
      onReset={reset}
      onFileChange={handleFileChange}
      onDownloadTemplate={downloadTemplate}
      onUpload={() => {
        if (!processing && rows.length) {
          onUpload(rows);
          onClose();
        }
      }}
      templateHeadersString={headers.join("\t")}
      templateInstructions={instructions}
      infoTitle='Prior YA CA, Losses and Donations'
      infoMessage={`Upload prior YA CA, losses and donations in one Excel file. Use CA, Losses or Donations under Section.`}
      fixedValueGroups={[
        { label: "Section", values: PRIOR_YA_SECTIONS },
        { label: "Forfeited / Declare Agreement Status / Agreed", values: ["", "TRUE", "FALSE"] },
      ]}
    />
  );
};

export default PriorYAExcelUpload;
